import angular, { IPromise } from "angular";
import { IAxleWeightProfile } from "@interfaces/IAxleWeightProfile";
import { IAxleWeightOutOfBoundsHistory, ProfileBound, VehicleAxle } from "@interfaces/IAxleWeightOutOfBoundsHistory";
import { IAlertRuleRecord } from "src/app/main/modules/alerts/_models/alerts.interfaces";
import { AxleWeightService } from "@services/axleWeight.service";
import { TranslationService } from "@services/translation.service";

export interface IAxleWeightAlertDetailData {
    profile?: IAxleWeightProfile;
    history?: IAxleWeightOutOfBoundsHistory;
    lowerBound?: number;
    upperBound?: number;
    weightDiffExtreme?: number;
    weightDiffMessageExtreme?: string;
}

export class AxleWeightAlertDetailService {
    static $inject = ["$q", "translationService", "axleWeightService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly translationService: TranslationService,
        private readonly axleWeightService: AxleWeightService) {
    }

    getAlertExport(record: IAlertRuleRecord): IPromise<string> {
        return this.getAlertData(record).then((data) => {
            return `[${data.history?.VehicleAxle}] ${this.createAlertLabel(data)}`;
        }).catch((error) => {
            console.error("Error retrieving axle weight alert detail: ", error);

            return null
        });
    }

    createAlertLabel(data: IAxleWeightAlertDetailData): string {
        return `${data.history?.AxleWeightExtreme} (${data.lowerBound} - ${data.upperBound})`;
    }

    getAlertData(record: IAlertRuleRecord): IPromise<IAxleWeightAlertDetailData> {
        const promises = [];
        const data: IAxleWeightAlertDetailData = {};

        promises.push(this.getAxleWeightProfile(record, data));
        promises.push(this.getAxleWeightHistory(record, data));

        return this.$q.all(promises).then(() => {
            data.lowerBound = this.getBound(data.profile, ProfileBound.Lower, data.history.VehicleAxle);
            data.upperBound = this.getBound(data.profile, ProfileBound.Upper, data.history.VehicleAxle);

            if (data.history.AxleWeightExtreme < data.lowerBound) {
                data.weightDiffExtreme = Math.abs(data.history.AxleWeightExtreme - data.lowerBound);
                data.weightDiffMessageExtreme = this.translationService.t('Alerts.BelowLowerBounds');
            } else {
                data.weightDiffExtreme = data.history.AxleWeightExtreme - data.upperBound;
                data.weightDiffMessageExtreme = this.translationService.t('Alerts.AboveUpperBounds');;
            }

            return data;
        });
    }

    private getAxleWeightProfile(record: IAlertRuleRecord, data: IAxleWeightAlertDetailData) : angular.IPromise<void> {
        if (!record.alertMetadata.AxleWeightProfileId) {
            return this.$q.resolve();
        }

        return this.axleWeightService.getAxleWeightProfile(record.alertMetadata.AxleWeightProfileId).then(profile => {
            data.profile = profile;
        });
    }

    private getAxleWeightHistory(record: IAlertRuleRecord, data: IAxleWeightAlertDetailData): angular.IPromise<void> {
        if (!record.alertMetadata.AxleWeightOutOfBoundsHistoryId) {
            return this.$q.resolve();
        }

        return this.axleWeightService
            .getAxleWeightOutOfBoundsHistory(record.alertMetadata.AxleWeightOutOfBoundsHistoryId).then(history => {
                data.history = history;
            });
    }

    private getBound(profile: IAxleWeightProfile, bound: ProfileBound, axle: VehicleAxle) :number {
        switch (axle) {
        case VehicleAxle.Total:
            return bound === ProfileBound.Upper ? profile.TotalUpperBound : profile.TotalLowerBound;
        case VehicleAxle.Axle1:
            return bound === ProfileBound.Upper ? profile.Axle1UpperBound : profile.Axle1LowerBound;
        case VehicleAxle.Axle2:
            return bound === ProfileBound.Upper ? profile.Axle2UpperBound : profile.Axle2LowerBound;
        case VehicleAxle.Axle3:
            return bound === ProfileBound.Upper ? profile.Axle3UpperBound : profile.Axle3LowerBound;
        case VehicleAxle.Axle4:
            return bound === ProfileBound.Upper ? profile.Axle4UpperBound : profile.Axle4LowerBound;
        default:
            return null;
        }
    }
}
